// Global Layer Properties — floating diagnostic/configuration console.
//
// A separate, gear-triggered floating panel (NOT a normal tool card) that
// renders the {@link LayerDiagnostics} view model across seven tabs:
// Overview, Source, Coordinates, Render Path, Output Files, Raw JSON, Preview.
//
// This component is purely presentational + a small local form for the session
// coordinate override. It is layer-KIND aware via the diagnostics it receives
// and never references a scene-specific layer id. All styling comes from the
// design tokens / primitives so it tracks the platform design system.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Callout,
  Icon,
  type IconName,
  Surface,
  Tabs,
  Text,
  tokens,
} from '@/design';
import type {
  CoordinateScope,
  LayerCoordinateDiagnostics,
  LayerDiagnostics,
  LayerHealthLabel,
  LayerOutputFilesDiagnostics,
  LayerOverviewDiagnostics,
  LayerPreview,
  LayerRawJson,
  LayerRenderPathDiagnostics,
  LayerSourceDiagnostics,
  LayerWarning,
} from '@/modules/layer-diagnostics';
import { LayerPreviewMap, hasPreviewableGeometry } from './LayerPreviewMap';
import { LayerPreview3D } from '@/engines/giro3d/preview/LayerPreview3D';
import { LayerPreviewModel } from '@/engines/giro3d/preview/LayerPreviewModel';
import { PmtilesControls } from './PmtilesControls';
import { RasterMetadataPanel } from '@/components/raster/RasterMetadataPanel';
import type { Layer } from '@geosoft/viewer-sdk';
import type { NativeVectorStylePatch } from '@/engines/giro3d/nativePmtilesVectorLayer';

/** Default floating size — wide enough for all seven tabs without scrolling. */
const DEFAULT_WIDTH = tokens.layout.standaloneStateWide;
const DEFAULT_HEIGHT = `min(80vh, calc(${tokens.layout.standaloneStateExtraWide}px + ${tokens.layout.popoverMd}px))`;
const VIEWPORT_MARGIN = tokens.layout.workspaceCanvasInset;

/**
 * Curated quick-pick CRSs for the render-frame override. `proj4` is supplied for
 * projected/local grids the engine cannot resolve from the code alone (so the
 * override is usable immediately); geographic/web frames the engine knows
 * natively carry `null`. The operator can always edit the result before
 * applying — this is only a convenience list, not an authority.
 */
interface CrsPreset {
  readonly code: string;
  readonly label: string;
  readonly proj4: string | null;
}
const CRS_PRESETS: ReadonlyArray<CrsPreset> = [
  { code: 'EPSG:4326', label: 'WGS 84 — geographic (lon/lat)', proj4: null },
  { code: 'EPSG:3857', label: 'Web Mercator', proj4: null },
  {
    code: 'EPSG:3997',
    label: 'Dubai Local TM',
    proj4:
      '+proj=tmerc +lat_0=0 +lon_0=55.33333333333333 +k=1 +x_0=500000 +y_0=0 +datum=WGS84 +units=m +no_defs',
  },
  {
    code: 'EPSG:28992',
    label: 'Amersfoort / RD New (NL)',
    proj4:
      '+proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 +k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel +units=m +no_defs',
  },
  {
    code: 'EPSG:25832',
    label: 'ETRS89 / UTM zone 32N',
    proj4: '+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs',
  },
  {
    code: 'EPSG:32640',
    label: 'WGS 84 / UTM zone 40N',
    proj4: '+proj=utm +zone=40 +datum=WGS84 +units=m +no_defs',
  },
  {
    code: 'EPSG:32636',
    label: 'WGS 84 / UTM zone 36N',
    proj4: '+proj=utm +zone=36 +datum=WGS84 +units=m +no_defs',
  },
  {
    code: 'EPSG:32736',
    label: 'WGS 84 / UTM zone 36S',
    proj4: '+proj=utm +zone=36 +south +datum=WGS84 +units=m +no_defs',
  },
];

export interface CoordinateOverrideValue {
  readonly sourceCrs: string | null;
  readonly displayCrs: string | null;
  readonly displayCrsProj4: string | null;
  /** Storage tier label shown to the operator (e.g. `session`). */
  readonly scope?: string;
}

export interface LayerPropertiesPanelProps {
  readonly diagnostics: LayerDiagnostics;
  /** Live catalog layer — powers the QGIS-style raster Information section. */
  readonly layer?: Layer | null;
  readonly onClose?: () => void;
  /** Active session override from the store (or null when none is set). */
  readonly coordinateOverride?: CoordinateOverrideValue | null;
  readonly onApplyCoordinateOverride?: (patch: {
    sourceCrs: string | null;
    displayCrs: string | null;
    displayCrsProj4: string | null;
  }) => void;
  readonly onClearCoordinateOverride?: () => void;
  readonly onPmtilesStyleChange?: (patch: NativeVectorStylePatch) => void;
  readonly onPmtilesSourceLayerChange?: (sourceLayer: string) => void;
  readonly 'data-testid'?: string;
}

type TabId =
  | 'overview'
  | 'source'
  | 'coordinates'
  | 'render-path'
  | 'output-files'
  | 'raw'
  | 'preview';

const TABS: ReadonlyArray<{ id: TabId; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'source', label: 'Source' },
  { id: 'coordinates', label: 'Coordinates' },
  { id: 'render-path', label: 'Render Path' },
  { id: 'output-files', label: 'Output Files' },
  { id: 'raw', label: 'Raw JSON' },
  { id: 'preview', label: 'Preview' },
];

export function LayerPropertiesPanel({
  diagnostics,
  layer = null,
  onClose,
  coordinateOverride,
  onApplyCoordinateOverride,
  onClearCoordinateOverride,
  onPmtilesStyleChange,
  onPmtilesSourceLayerChange,
  'data-testid': testId = 'layer-properties-panel',
}: LayerPropertiesPanelProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [copied, setCopied] = useState<string | null>(null);
  const { pos, containerRef, dragHandlers } = usePanelDragResize();

  const copy = (label: string, value: string | null | undefined) => {
    if (!value) return;
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(label);
      window.setTimeout(() => setCopied(null), 1200);
    });
  };

  const { overview, warnings } = diagnostics;
  const subtitle = overview.label ?? overview.id;

  return (
    <div
      ref={containerRef}
      data-testid={`${testId}-frame`}
      style={{
        position: 'fixed',
        // Before first measurement, anchor to the top-right; afterwards use the
        // dragged left/top so the panel can be moved anywhere on screen.
        top: pos ? pos.top : VIEWPORT_MARGIN,
        left: pos ? pos.left : undefined,
        right: pos ? undefined : VIEWPORT_MARGIN,
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
        minWidth: 320,
        minHeight: 280,
        maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
        maxHeight: `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`,
        // Native resize grip (bottom-right): lets the operator adjust width/height.
        resize: 'both',
        overflow: 'hidden',
        display: 'flex',
        borderRadius: tokens.radius.lg,
        boxShadow: tokens.elevation[3],
        zIndex: tokens.zIndex.dock + 2,
      }}
    >
    <Surface
      level={1}
      elevation={3}
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 0,
        border: tokens.border.thin,
        borderRadius: tokens.radius.lg,
        overflow: 'hidden',
      }}
    >
      <PanelHeader
        subtitle={subtitle}
        health={overview.health}
        warningCount={warnings.length}
        onClose={onClose}
        dragHandlers={dragHandlers}
      />

      {warnings.length > 0 ? (
        <div
          style={{
            flex: '0 0 auto',
            maxHeight: 168,
            overflow: 'auto',
            padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.space[2],
            borderBottom: tokens.border.thin,
            background: tokens.color.surface[2],
          }}
        >
          {warnings.map((w) => (
            <WarningCallout key={w.code} warning={w} />
          ))}
        </div>
      ) : null}

      <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
        <Tabs
          tabs={TABS as { id: string; label: string }[]}
          value={activeTab}
          onChange={(next) => setActiveTab(next as TabId)}
          aria-label="Layer property sections"
          data-testid={`${testId}-tabs`}
          tablistStyle={{ flexWrap: 'wrap', rowGap: tokens.space[1] }}
        >
          {activeTab === 'overview' ? (
            <OverviewTab overview={overview} layer={layer} onCopy={copy} />
          ) : null}
          {activeTab === 'source' ? (
            <SourceTab
              source={diagnostics.source}
              renderPath={diagnostics.renderPath}
              onCopy={copy}
              onPmtilesStyleChange={onPmtilesStyleChange}
              onPmtilesSourceLayerChange={onPmtilesSourceLayerChange}
            />
          ) : null}
          {activeTab === 'coordinates' ? (
            <CoordinatesTab
              coordinates={diagnostics.coordinates}
              warnings={warnings}
              override={coordinateOverride ?? null}
              onApply={onApplyCoordinateOverride}
              onClear={onClearCoordinateOverride}
              onCopy={copy}
            />
          ) : null}
          {activeTab === 'render-path' ? (
            <RenderPathTab renderPath={diagnostics.renderPath} onCopy={copy} />
          ) : null}
          {activeTab === 'output-files' ? (
            <OutputFilesTab outputFiles={diagnostics.outputFiles} onCopy={copy} />
          ) : null}
          {activeTab === 'raw' ? <RawJsonTab raw={diagnostics.raw} onCopy={copy} /> : null}
          {activeTab === 'preview' ? <PreviewTab preview={diagnostics.preview} onCopy={copy} /> : null}
        </Tabs>
      </div>

      {copied ? (
        <div
          role="status"
          style={{
            flex: '0 0 auto',
            padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
            color: tokens.color.semantic.success,
            fontSize: tokens.font.size.xs,
            borderTop: tokens.border.thin,
            background: tokens.color.surface[2],
          }}
        >
          Copied {copied}
        </div>
      ) : null}
    </Surface>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drag + resize (self-contained floating panel)
// ---------------------------------------------------------------------------

interface DragHandlers {
  readonly onPointerDown: (e: React.PointerEvent) => void;
  readonly onPointerMove: (e: React.PointerEvent) => void;
  readonly onPointerUp: (e: React.PointerEvent) => void;
}

/**
 * Make the panel draggable by its header and freely resizable (the container's
 * native `resize: both` grip handles sizing; this hook only tracks position).
 * Position is clamped so the header stays on-screen and grabbable.
 */
function usePanelDragResize(): {
  pos: { left: number; top: number } | null;
  containerRef: React.RefObject<HTMLDivElement>;
  dragHandlers: DragHandlers;
} {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const drag = useRef<
    { startX: number; startY: number; startLeft: number; startTop: number } | null
  >(null);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    // Don't start a drag from an interactive control in the header (close button).
    if ((e.target as HTMLElement).closest('button, input, a, [role="button"]')) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    drag.current = {
      startX: e.clientX,
      startY: e.clientY,
      startLeft: rect.left,
      startTop: rect.top,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const width = containerRef.current?.offsetWidth ?? DEFAULT_WIDTH;
    const nextLeft = d.startLeft + (e.clientX - d.startX);
    const nextTop = d.startTop + (e.clientY - d.startY);
    // Keep at least a sliver of the panel (and all of the header) reachable.
    const maxLeft = window.innerWidth - Math.min(width, 160);
    const maxTop = window.innerHeight - 48;
    setPos({
      left: Math.min(Math.max(0, nextLeft), Math.max(0, maxLeft)),
      top: Math.min(Math.max(0, nextTop), Math.max(0, maxTop)),
    });
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  }, []);

  return { pos, containerRef, dragHandlers: { onPointerDown, onPointerMove, onPointerUp } };
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function PanelHeader({
  subtitle,
  health,
  warningCount,
  onClose,
  dragHandlers,
}: {
  readonly subtitle: string;
  readonly health: LayerHealthLabel;
  readonly warningCount: number;
  readonly onClose?: () => void;
  readonly dragHandlers?: DragHandlers;
}) {
  return (
    <div
      {...dragHandlers}
      style={{
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        gap: tokens.space[2],
        padding: `${tokens.space[3]}px`,
        borderBottom: tokens.border.thin,
        background: tokens.color.surface[1],
        cursor: dragHandlers ? 'move' : undefined,
        touchAction: dragHandlers ? 'none' : undefined,
        userSelect: 'none',
      }}
    >
      <Icon name="drag_indicator" size={16} decorative />
      <Icon name="settings" size={20} decorative />
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <Text variant="body" style={{ fontWeight: tokens.font.weight.semibold }}>
          Layer properties
        </Text>
        <span
          title={subtitle}
          style={{
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.xs,
            fontFamily: tokens.font.family.mono,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {subtitle}
        </span>
      </div>
      {warningCount > 0 ? (
        <span
          title={`${warningCount} diagnostic ${warningCount === 1 ? 'finding' : 'findings'}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
            padding: `0 ${tokens.space[2]}px`,
            height: tokens.density.rowDense - tokens.space[2],
            borderRadius: tokens.radius.pill,
            background: tokens.color.surface[2],
            color: tokens.color.semantic.warning,
            fontSize: tokens.font.size.xs,
            fontWeight: tokens.font.weight.medium,
          }}
        >
          <Icon name="warning" size={16} decorative />
          {warningCount}
        </span>
      ) : null}
      <HealthChip health={health} />
      {onClose ? (
        <button
          type="button"
          aria-label="Close layer properties"
          onClick={onClose}
          style={{
            width: tokens.density.rowDense,
            height: tokens.density.rowDense,
            border: tokens.border.thin,
            borderRadius: tokens.radius.pill,
            background: tokens.color.surface[1],
            color: tokens.color.ink.secondary,
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
          }}
        >
          <Icon name="close" size={16} decorative />
        </button>
      ) : null}
    </div>
  );
}

const HEALTH_COLOR: Record<LayerHealthLabel, string> = {
  ready: tokens.color.semantic.success,
  loading: tokens.color.semantic.info,
  pending: tokens.color.semantic.info,
  deferred: tokens.color.semantic.info,
  failed: tokens.color.semantic.danger,
  unsupported: tokens.color.semantic.warning,
  unknown: tokens.color.ink.muted,
};

function HealthChip({ health }: { readonly health: LayerHealthLabel }) {
  return (
    <span
      data-testid="layer-properties-health"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.space[1],
        padding: `0 ${tokens.space[2]}px`,
        height: tokens.density.rowDense - tokens.space[2],
        borderRadius: tokens.radius.pill,
        border: tokens.border.thin,
        background: tokens.color.surface[2],
        color: tokens.color.ink.secondary,
        fontSize: tokens.font.size.xs,
        fontWeight: tokens.font.weight.medium,
        textTransform: 'capitalize',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: tokens.radius.pill,
          background: HEALTH_COLOR[health],
        }}
      />
      {health}
    </span>
  );
}

function WarningCallout({ warning }: { readonly warning: LayerWarning }) {
  const scopeLabel =
    warning.scope === 'producer'
      ? 'Producer-side'
      : warning.scope === 'converter'
        ? 'Converter-side'
        : 'Viewer';
  return (
    <Callout severity={warning.severity} title={warning.title} data-testid={`warning-${warning.code}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}>
        <span>{warning.detail}</span>
        {warning.recommendedFix ? (
          <span style={{ color: tokens.color.ink.secondary }}>
            <strong>{scopeLabel} fix:</strong> {warning.recommendedFix}
          </span>
        ) : (
          <span style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>
            {scopeLabel} · {warning.code}
          </span>
        )}
      </div>
    </Callout>
  );
}

// ---------------------------------------------------------------------------
// Shared row / section primitives
// ---------------------------------------------------------------------------

function Section({ title, children }: { readonly title: string; readonly children: React.ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[2], marginBottom: tokens.space[4] }}>
      <Text
        variant="caption"
        as="h3"
        style={{
          margin: 0,
          color: tokens.color.ink.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontSize: tokens.font.size.xs,
        }}
      >
        {title}
      </Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[2] }}>{children}</div>
    </section>
  );
}

function Row({
  label,
  value,
  mono = false,
  onCopy,
  copyLabel,
  copyValue,
}: {
  readonly label: string;
  readonly value: React.ReactNode;
  readonly mono?: boolean;
  readonly onCopy?: (label: string, value: string | null | undefined) => void;
  readonly copyLabel?: string;
  readonly copyValue?: string | null;
}) {
  const display = value === null || value === undefined || value === '' ? null : value;
  const titleText =
    typeof copyValue === 'string'
      ? copyValue
      : typeof display === 'string'
        ? display
        : undefined;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${tokens.layout.settingsRail / 2}px minmax(0, 1fr) auto`,
        gap: tokens.space[2],
        alignItems: 'baseline',
      }}
    >
      <span style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>{label}</span>
      <span
        title={titleText}
        style={{
          color: display === null ? tokens.color.ink.muted : tokens.color.ink.primary,
          fontFamily: mono ? tokens.font.family.mono : tokens.font.family.ui,
          fontSize: tokens.font.size.xs,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {display ?? 'Not available'}
      </span>
      {onCopy ? (
        <button
          type="button"
          disabled={!copyValue}
          aria-label={`Copy ${label}`}
          onClick={() => onCopy(copyLabel ?? label, copyValue)}
          style={{
            border: tokens.border.thin,
            borderRadius: tokens.radius.pill,
            background: tokens.color.surface[1],
            color: copyValue ? tokens.color.ink.secondary : tokens.color.ink.muted,
            cursor: copyValue ? 'pointer' : 'not-allowed',
            fontSize: tokens.font.size.xs,
            padding: `0 ${tokens.space[2]}px`,
            height: tokens.density.rowDense - tokens.space[2],
          }}
        >
          Copy
        </button>
      ) : null}
    </div>
  );
}

function BoolText({ value }: { readonly value: boolean | null }) {
  if (value === null) return <>Unknown</>;
  return <>{value ? 'Yes' : 'No'}</>;
}

type CopyFn = (label: string, value: string | null | undefined) => void;

// ---------------------------------------------------------------------------
// Tab: Overview
// ---------------------------------------------------------------------------

function OverviewTab({
  overview,
  layer,
  onCopy,
}: {
  readonly overview: LayerOverviewDiagnostics;
  readonly layer?: Layer | null;
  readonly onCopy: CopyFn;
}) {
  const showRasterInfo =
    layer
    && (layer.kind === 'raster' || layer.kind === 'terrain'
      || Boolean(layer.endpoints?.tiles?.includes('/cog/'))
      || Boolean(layer.endpoints?.terrain?.includes('/cog/')));

  return (
    <div>
      <Section title="Identity">
        <Row label="Layer ID" value={overview.id} mono onCopy={onCopy} copyValue={overview.id} />
        <Row label="Label" value={overview.label} />
        <Row label="Kind" value={overview.kind} mono />
        <Row label="Slot" value={overview.slot} mono />
        <Row label="Scene profile" value={overview.sceneProfile} />
        <Row label="Activation tier" value={overview.activationTier} />
        <Row label="Visual purpose" value={overview.visualPurpose} />
      </Section>
      {showRasterInfo ? (
        <Section title="Raster information">
          <RasterMetadataPanel layer={layer} embedded data-testid="layer-properties-raster-metadata" />
        </Section>
      ) : null}
      <Section title="State">
        <Row label="Store visible" value={<BoolText value={overview.storeVisible} />} />
        <Row label="Engine visible" value={<BoolText value={overview.engineVisible} />} />
        <Row label="Mounted" value={<BoolText value={overview.mounted} />} />
        <Row label="Health" value={<span style={{ textTransform: 'capitalize' }}>{overview.health}</span>} />
      </Section>
      <Section title="Render">
        <Row label="Adapter owner" value={overview.adapterOwner} mono />
        <Row label="Render path" value={overview.renderPathLabel} />
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Source
// ---------------------------------------------------------------------------

function SourceTab({
  source,
  renderPath,
  onCopy,
  onPmtilesStyleChange,
  onPmtilesSourceLayerChange,
}: {
  readonly source: LayerSourceDiagnostics;
  readonly renderPath: LayerRenderPathDiagnostics;
  readonly layer?: Layer | null;
  readonly onCopy: CopyFn;
  readonly onPmtilesStyleChange?: (patch: NativeVectorStylePatch) => void;
  readonly onPmtilesSourceLayerChange?: (sourceLayer: string) => void;
}) {
  const hasPmtilesControls =
    source.pmtilesArchive !== null ||
    source.selectedPmtilesSourceLayer !== null ||
    (source.availablePmtilesSourceLayers?.length ?? 0) > 0;

  return (
    <div>
      <Section title="Artifact">
        <Row label="Artifact kind" value={source.artifact.artifactKind} mono />
        <Row label="Classified" value={source.artifact.classified} mono />
        <Row label="Description" value={source.artifact.description} />
        <Row label="Estimated size" value={source.estimatedSize} />
        <Row label="Feature / object count" value={source.featureOrObjectCount} />
        {source.selectedPmtilesSourceLayer !== null ? (
          <Row label="PMTiles source-layer (applied)" value={source.selectedPmtilesSourceLayer} mono />
        ) : null}
        {source.declaredPmtilesSourceLayer &&
        source.declaredPmtilesSourceLayer !== source.selectedPmtilesSourceLayer ? (
          <Row
            label="Declared source-layer"
            value={
              <span style={{ color: tokens.color.semantic.warning }}>
                {source.declaredPmtilesSourceLayer} (not in archive)
              </span>
            }
            mono
          />
        ) : null}
        {source.availablePmtilesSourceLayers && source.availablePmtilesSourceLayers.length > 0 ? (
          <Row
            label="Archive layers"
            value={source.availablePmtilesSourceLayers.join(', ')}
            mono
          />
        ) : null}
        {source.pmtilesArchive ? (
          <>
            <Row label="Archive tile type" value={source.pmtilesArchive.tileType ?? null} mono />
            <Row
              label="Archive zooms"
              value={
                typeof source.pmtilesArchive.minZoom === 'number' &&
                typeof source.pmtilesArchive.maxZoom === 'number'
                  ? `${source.pmtilesArchive.minZoom} - ${source.pmtilesArchive.maxZoom}`
                  : null
              }
              mono
            />
            <Row
              label="Archive bounds"
              value={source.pmtilesArchive.bounds?.map((n) => n.toFixed(6)).join(', ') ?? null}
              mono
            />
            <Row
              label="Archive tile entries"
              value={
                typeof source.pmtilesArchive.numTileEntries === 'number'
                  ? source.pmtilesArchive.numTileEntries.toLocaleString()
                  : null
              }
            />
          </>
        ) : null}
      </Section>

      {hasPmtilesControls ? (
        <Section title="PMTiles Controls">
          <PmtilesControls
            source={source}
            renderPath={renderPath}
            onStyleChange={onPmtilesStyleChange}
            onSourceLayerChange={onPmtilesSourceLayerChange}
          />
        </Section>
      ) : null}

      {source.artifact.details.length > 0 ? (
        <Section title="Metadata">
          {source.artifact.details.map((d) => (
            <Row key={d.label} label={d.label} value={d.value} mono />
          ))}
        </Section>
      ) : null}

      <Section title="Endpoints">
        {source.endpoints.length === 0 ? (
          <Text variant="body" style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>
            No endpoints declared for this layer.
          </Text>
        ) : (
          source.endpoints.map((ep) => (
            <Row
              key={ep.key}
              label={ep.key}
              value={ep.url}
              mono
              onCopy={onCopy}
              copyLabel={`${ep.key} endpoint`}
              copyValue={ep.url}
            />
          ))
        )}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Coordinates (+ session override editor)
// ---------------------------------------------------------------------------

function CoordinatesTab({
  coordinates,
  warnings,
  override,
  onApply,
  onClear,
  onCopy,
}: {
  readonly coordinates: LayerCoordinateDiagnostics;
  readonly warnings: ReadonlyArray<LayerWarning>;
  readonly override: CoordinateOverrideValue | null;
  readonly onApply?: (patch: { sourceCrs: string | null; displayCrs: string | null; displayCrsProj4: string | null }) => void;
  readonly onClear?: () => void;
  readonly onCopy: CopyFn;
}) {
  const COORD_WARNING_CODES = new Set([
    'LP_W_MISSING_DISPLAY_CRS',
    'LP_W_DISPLAY_CRS_NO_PROJ4',
    'LP_W_DISPLAY_CRS_IGNORED_RASTER',
    'LP_W_LAYER_FRAME_MISMATCH',
    'LP_W_ALIGNMENT_REJECTED',
    'LP_W_MISSING_ANCHOR',
    'LP_W_3DTILES_ORIGIN_ANCHOR',
    'LP_W_DEFAULT_BBOX',
  ]);
  const coordinateWarnings = warnings.filter((w) => COORD_WARNING_CODES.has(w.code));
  const scopes = coordinates.scopes;
  return (
    <div>
      <Section title="Coordinate system (viewer · group · layer)">
        {scopes.mismatch ? (
          <Callout severity="warning" title="Coordinate systems disagree">
            The viewer frame, group, and layer don&apos;t all share one CRS. Geometry, basemap,
            and 3D content can drift apart until they&apos;re reconciled into a single frame.
          </Callout>
        ) : null}
        <ScopeRow scope={scopes.viewer} onCopy={onCopy} />
        <ScopeRow scope={scopes.group} onCopy={onCopy} />
        <ScopeRow scope={scopes.layer} emphasize onCopy={onCopy} />
      </Section>

      <Section title="Coordinate reference">
        <Row label="Source CRS" value={coordinates.sourceCrs} mono onCopy={onCopy} copyValue={coordinates.sourceCrs} />
        <Row label="Assigned source CRS" value={coordinates.assignedSourceCrs} mono />
        <Row label="Display CRS" value={coordinates.displayCrs} mono />
        <Row label="proj4 present" value={<BoolText value={coordinates.displayCrsProj4Present} />} />
        <Row label="Frame CRS" value={coordinates.frameCrs} mono />
        <Row label="World frame" value={coordinates.worldFrame} mono />
        <Row label="Vertical datum" value={coordinates.verticalDatum} mono />
      </Section>

      <Section title="Extent & alignment">
        <Row label="BBox assumption" value={coordinates.bboxAssumption} />
        <Row label="BBox" value={coordinates.bbox} mono onCopy={onCopy} copyValue={coordinates.bbox} />
        <Row label="BBox center" value={coordinates.bboxCenter} mono />
        <Row label="Anchor" value={coordinates.anchor} mono />
        <Row label="Alignment status" value={coordinates.alignmentStatus} mono />
        <Row
          label="Applied scale"
          value={coordinates.appliedScale === null ? null : coordinates.appliedScale.toFixed(4)}
          mono
        />
        <Row label="Render frame" value={coordinates.renderFrame} mono />
      </Section>

      {coordinateWarnings.length > 0 ? (
        <Section title="Coordinate findings">
          {coordinateWarnings.map((w) => (
            <WarningCallout key={w.code} warning={w} />
          ))}
        </Section>
      ) : null}

      <CoordinateOverrideEditor
        active={coordinates.override.active}
        scope={override?.scope ?? 'session'}
        value={{
          sourceCrs: override?.sourceCrs ?? coordinates.override.sourceCrs,
          displayCrs: override?.displayCrs ?? coordinates.override.displayCrs,
          displayCrsProj4: override?.displayCrsProj4 ?? coordinates.override.displayCrsProj4,
        }}
        sceneCrsOptions={[
          coordinates.sourceCrs,
          coordinates.frameCrs,
          scopes.viewer?.crs,
          scopes.group?.crs,
          scopes.layer?.crs,
        ]}
        onApply={onApply}
        onClear={onClear}
      />
    </div>
  );
}

const SCOPE_ICON: Record<CoordinateScope['level'], IconName> = {
  viewer: 'public',
  group: 'layers',
  layer: 'location_on',
};

/** One row of the viewer / group / layer coordinate-system readout. */
function ScopeRow({
  scope,
  emphasize = false,
  onCopy,
}: {
  readonly scope: CoordinateScope | null;
  readonly emphasize?: boolean;
  readonly onCopy?: CopyFn;
}) {
  if (!scope) return null;
  const hasCrs = Boolean(scope.crs);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${tokens.space[5]}px ${tokens.layout.popoverSm}px minmax(0, 1fr) auto`,
        gap: tokens.space[2],
        alignItems: 'baseline',
        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
        borderRadius: tokens.radius.md,
        background: emphasize ? tokens.color.surface[2] : 'transparent',
      }}
    >
      <Icon name={SCOPE_ICON[scope.level]} size={16} decorative />
      <span style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>{scope.label}</span>
      <span style={{ minWidth: 0 }}>
        <span
          title={scope.crs ?? undefined}
          style={{
            display: 'block',
            color: hasCrs ? tokens.color.ink.primary : tokens.color.ink.muted,
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.size.xs,
            fontWeight: emphasize ? tokens.font.weight.semibold : tokens.font.weight.regular,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {scope.crs ?? 'Unknown'}
        </span>
        {scope.note ? (
          <span style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>{scope.note}</span>
        ) : null}
      </span>
      {onCopy ? (
        <button
          type="button"
          disabled={!hasCrs}
          aria-label={`Copy ${scope.label} CRS`}
          onClick={() => onCopy(`${scope.label} CRS`, scope.crs)}
          style={{
            border: tokens.border.thin,
            borderRadius: tokens.radius.pill,
            background: tokens.color.surface[1],
            color: hasCrs ? tokens.color.ink.secondary : tokens.color.ink.muted,
            cursor: hasCrs ? 'pointer' : 'not-allowed',
            fontSize: tokens.font.size.xs,
            padding: `0 ${tokens.space[2]}px`,
            height: tokens.density.rowDense - tokens.space[2],
          }}
        >
          Copy
        </button>
      ) : null}
    </div>
  );
}

function CoordinateOverrideEditor({
  active,
  scope,
  value,
  sceneCrsOptions,
  onApply,
  onClear,
}: {
  readonly active: boolean;
  readonly scope: string;
  readonly value: { sourceCrs: string | null; displayCrs: string | null; displayCrsProj4: string | null };
  readonly sceneCrsOptions?: ReadonlyArray<string | null | undefined>;
  readonly onApply?: (patch: { sourceCrs: string | null; displayCrs: string | null; displayCrsProj4: string | null }) => void;
  readonly onClear?: () => void;
}) {
  const [sourceCrs, setSourceCrs] = useState(value.sourceCrs ?? '');
  const [crs, setCrs] = useState(value.displayCrs ?? '');
  const [proj4, setProj4] = useState(value.displayCrsProj4 ?? '');

  // Re-seed local inputs when the upstream override changes (e.g. another
  // surface edited it, or the panel switched to a different layer).
  useEffect(() => {
    setSourceCrs(value.sourceCrs ?? '');
    setCrs(value.displayCrs ?? '');
    setProj4(value.displayCrsProj4 ?? '');
  }, [value.sourceCrs, value.displayCrs, value.displayCrsProj4]);

  if (!onApply) return null;

  // Scene CRSs already in play (source/frame/viewer/group/layer), minus the
  // ones already covered by the curated preset list and minus blanks.
  const sceneOptions = Array.from(
    new Set((sceneCrsOptions ?? []).filter((c): c is string => Boolean(c))),
  ).filter((c) => !CRS_PRESETS.some((p) => p.code === c));

  // Quick-pick: fill the CRS (and proj4 when known) from a preset / scene CRS.
  const pick = (code: string) => {
    if (!code || code === '__custom__') return;
    setCrs(code);
    const preset = CRS_PRESETS.find((p) => p.code === code);
    if (preset?.proj4) setProj4(preset.proj4);
  };

  const selectValue =
    CRS_PRESETS.some((p) => p.code === crs) || sceneOptions.includes(crs) ? crs : '__custom__';

  const inputStyle: React.CSSProperties = {
    width: '100%',
    border: tokens.border.thin,
    borderRadius: tokens.radius.md,
    background: tokens.color.surface[1],
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.mono,
    fontSize: tokens.font.size.xs,
    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
    height: tokens.density.rowDense,
    boxSizing: 'border-box',
  };

  return (
    <Section title="Coordinate assignment and render frame">
      <Callout
        severity={active ? 'warning' : 'info'}
        title={active ? `Override active (${scope})` : 'No override set'}
      >
        {active
          ? `This layer is using an operator override. Overrides are ${scope}-scoped and are not persisted to the converter or gTwin.`
          : 'Assign a source CRS when the existing coordinates are correct but the file metadata is missing or wrong. This reinterprets coordinates; it does not rewrite or reproject the file.'}
      </Callout>
      <label style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}>
        <span style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>Assign source CRS</span>
        <input
          value={sourceCrs}
          onChange={(e) => setSourceCrs(e.target.value)}
          placeholder="e.g. EPSG:32636"
          aria-label="Assign source CRS"
          style={inputStyle}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}>
        <span style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>Quick pick</span>
        <select
          value={selectValue}
          aria-label="Quick-pick display CRS"
          onChange={(e) => pick(e.target.value)}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          <option value="__custom__">Custom…</option>
          {sceneOptions.length > 0 ? (
            <optgroup label="In this scene">
              {sceneOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </optgroup>
          ) : null}
          <optgroup label="Common systems">
            {CRS_PRESETS.map((p) => (
              <option key={p.code} value={p.code}>
                {p.code} — {p.label}
              </option>
            ))}
          </optgroup>
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}>
        <span style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>Display CRS</span>
        <input
          value={crs}
          onChange={(e) => setCrs(e.target.value)}
          placeholder="e.g. EPSG:28992"
          aria-label="Override display CRS"
          style={inputStyle}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}>
        <span style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>proj4 definition (optional)</span>
        <input
          value={proj4}
          onChange={(e) => setProj4(e.target.value)}
          placeholder="+proj=sterea +lat_0=52.156…"
          aria-label="Override proj4 definition"
          style={inputStyle}
        />
      </label>
      <div style={{ display: 'flex', gap: tokens.space[2] }}>
        <Button
          variant="secondary"
          onClick={() =>
            onApply({
              sourceCrs: sourceCrs.trim().length > 0 ? sourceCrs.trim() : null,
              displayCrs: crs.trim().length > 0 ? crs.trim() : null,
              displayCrsProj4: proj4.trim().length > 0 ? proj4.trim() : null,
            })
          }
        >
          Apply (session)
        </Button>
        {onClear ? (
          <Button variant="ghost" onClick={onClear} disabled={!active}>
            Clear
          </Button>
        ) : null}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Tab: Render Path
// ---------------------------------------------------------------------------

function RenderPathTab({
  renderPath,
  onCopy,
}: {
  readonly renderPath: LayerRenderPathDiagnostics;
  readonly onCopy: CopyFn;
}) {
  const syncLabel =
    renderPath.visibilityInSync === null
      ? 'Unknown'
      : renderPath.visibilityInSync
        ? 'In sync'
        : 'Out of sync';
  return (
    <div>
      <Section title="Routing">
        <Row label="Adapter id" value={renderPath.adapterId} mono />
        <Row label="Render path" value={renderPath.renderPathLabel} />
        <Row
          label="Expected adapter"
          value={renderPath.expectedAdapter}
          mono
        />
        <Row label="Live diagnostics" value={<BoolText value={renderPath.live} />} />
        {!renderPath.live ? (
          <Text variant="body" style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>
            Showing the expected path resolved from layer kind — the layer is not currently mounted in an engine.
          </Text>
        ) : null}
      </Section>

      <Section title="Mount & visibility">
        <Row label="Mounted" value={<BoolText value={renderPath.mounted} />} />
        <Row label="Store visible" value={<BoolText value={renderPath.storeVisible} />} />
        <Row label="Engine visible" value={<BoolText value={renderPath.engineVisible} />} />
        <Row
          label="Visibility"
          value={
            <span
              style={{
                color:
                  renderPath.visibilityInSync === false
                    ? tokens.color.semantic.warning
                    : tokens.color.ink.primary,
              }}
            >
              {syncLabel}
            </span>
          }
        />
      </Section>

      <Section title="Engine objects">
        {renderPath.engineLayerIds.length === 0 && renderPath.engineObjectIds.length === 0 ? (
          <Text variant="body" style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>
            No engine layer/object ids reported.
          </Text>
        ) : (
          <>
            {renderPath.engineLayerIds.map((id, i) => (
              <Row
                key={`l-${id}`}
                label={i === 0 ? 'Layer ids' : ''}
                value={id}
                mono
                onCopy={onCopy}
                copyLabel="Engine layer id"
                copyValue={id}
              />
            ))}
            {renderPath.engineObjectIds.map((id, i) => (
              <Row
                key={`o-${id}`}
                label={i === 0 ? 'Object ids' : ''}
                value={id}
                mono
                onCopy={onCopy}
                copyLabel="Engine object id"
                copyValue={id}
              />
            ))}
          </>
        )}
      </Section>

      {renderPath.decodedTileCount !== null ||
      renderPath.renderedFeatureCount !== null ||
      renderPath.lastTileError ||
      renderPath.renderFrameAssumptions.length > 0 ? (
        <Section title="PMTiles native vector diagnostics">
          <Row label="Decoded tile count" value={renderPath.decodedTileCount?.toLocaleString() ?? null} />
          <Row label="Rendered feature count" value={renderPath.renderedFeatureCount?.toLocaleString() ?? null} />
          {renderPath.lastTileError ? (
            <Callout severity="warning" title="Last tile error">
              {renderPath.lastTileError}
            </Callout>
          ) : null}
          {renderPath.renderFrameAssumptions.length > 0 ? (
            <div style={{ display: 'grid', gap: tokens.space[1] }}>
              {renderPath.renderFrameAssumptions.map((assumption) => (
                <Text
                  key={assumption}
                  variant="body"
                  style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}
                >
                  {assumption}
                </Text>
              ))}
            </div>
          ) : null}
        </Section>
      ) : null}

      {renderPath.lastError ? (
        <Section title="Last error">
          <Callout severity="error" title="Render / health error">
            {renderPath.lastError}
          </Callout>
        </Section>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Output Files
// ---------------------------------------------------------------------------

function OutputFilesTab({
  outputFiles,
  onCopy,
}: {
  readonly outputFiles: LayerOutputFilesDiagnostics;
  readonly onCopy: CopyFn;
}) {
  return (
    <div>
      {!outputFiles.hasConverterMetadata ? (
        <Callout severity="info" title="No converter metadata discoverable">
          This layer carries no QC manifest or provenance sidecar. To make output files inspectable, have the
          producer/converter emit a manifest alongside the primary artifact.
        </Callout>
      ) : null}
      <Section title={`Files (${outputFiles.files.length})`}>
        {outputFiles.files.length === 0 ? (
          <Text variant="body" style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>
            No output files resolved from this layer's endpoints or metadata.
          </Text>
        ) : (
          outputFiles.files.map((f) => (
            <Row
              key={`${f.role}-${f.url}`}
              label={`${f.label}${f.derived ? ' (derived)' : ''}`}
              value={f.url}
              mono
              onCopy={onCopy}
              copyLabel={f.label}
              copyValue={f.url}
            />
          ))
        )}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Raw JSON
// ---------------------------------------------------------------------------

function RawJsonTab({ raw, onCopy }: { readonly raw: LayerRawJson; readonly onCopy: CopyFn }) {
  return (
    <div>
      <JsonBlock title="Viewer layer JSON" value={raw.layerJson} onCopy={onCopy} />
      {raw.sceneItemJson != null ? (
        <JsonBlock title="gTwin scene item JSON" value={raw.sceneItemJson} onCopy={onCopy} />
      ) : null}
      {raw.engineHealthJson != null ? (
        <JsonBlock title="Engine health JSON" value={raw.engineHealthJson} onCopy={onCopy} />
      ) : null}
    </div>
  );
}

function JsonBlock({
  title,
  value,
  onCopy,
}: {
  readonly title: string;
  readonly value: unknown;
  readonly onCopy: CopyFn;
}) {
  let serialized: string;
  try {
    serialized = JSON.stringify(value, null, 2) ?? 'null';
  } catch {
    serialized = '/* value could not be serialized */';
  }
  return (
    <Section title={title}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          aria-label={`Copy ${title}`}
          onClick={() => onCopy(title, serialized)}
          style={{
            border: tokens.border.thin,
            borderRadius: tokens.radius.pill,
            background: tokens.color.surface[1],
            color: tokens.color.ink.secondary,
            cursor: 'pointer',
            fontSize: tokens.font.size.xs,
            padding: `0 ${tokens.space[2]}px`,
            height: tokens.density.rowDense - tokens.space[2],
          }}
        >
          Copy JSON
        </button>
      </div>
      <pre
        style={{
          margin: 0,
          maxHeight: 260,
          overflow: 'auto',
          padding: tokens.space[2],
          border: tokens.border.thin,
          borderRadius: tokens.radius.md,
          background: tokens.color.surface[2],
          color: tokens.color.ink.primary,
          fontFamily: tokens.font.family.mono,
          fontSize: tokens.font.size.xs,
          lineHeight: tokens.font.lineHeight.tight,
          whiteSpace: 'pre',
        }}
      >
        {serialized}
      </pre>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Tab: Preview
// ---------------------------------------------------------------------------

/**
 * Turn a raw single-band DEM tile template (TiTiler `.../{z}/{x}/{y}.png?...`)
 * into a *visual* one by adding a colormap + rescale. Without this the preview
 * would render raw elevation values (mostly black). Defensive: only appends
 * params that aren't already present, and the preview swallows tile errors.
 */
function withTerrainStyling(
  url: string,
  minmax: readonly [number, number] | null | undefined,
  colormap: string | null | undefined,
): string {
  let out = url;
  const sep = () => (out.includes('?') ? '&' : '?');
  if (!/[?&]colormap_name=/.test(out) && !/[?&]colormap=/.test(out)) {
    out += `${sep()}colormap_name=${encodeURIComponent(colormap && colormap.trim() ? colormap : 'terrain')}`;
  }
  if (minmax && Number.isFinite(minmax[0]) && Number.isFinite(minmax[1]) && !/[?&]rescale=/.test(out)) {
    out += `${sep()}rescale=${minmax[0]},${minmax[1]}`;
  }
  return out;
}

function PreviewTab({ preview, onCopy }: { readonly preview: LayerPreview; readonly onCopy: CopyFn }) {
  switch (preview.kind) {
    case 'extent':
      return (
        <div>
          {hasPreviewableGeometry(preview.bbox, null) ? (
            <Section title="2D preview">
              <LayerPreviewMap bbox={preview.bbox} center={preview.center} pmtiles={null} />
              <PreviewMapCaption />
            </Section>
          ) : null}
          <Section title="Extent preview">
            {preview.bbox ? <ExtentSketch bbox={preview.bbox} /> : null}
            <Row
              label="BBox"
              value={preview.bbox ? preview.bbox.map((n) => n.toFixed(5)).join(', ') : null}
              mono
              onCopy={onCopy}
              copyValue={preview.bbox ? preview.bbox.join(', ') : null}
            />
            <Row
              label="Center"
              value={preview.center ? preview.center.map((n) => n.toFixed(5)).join(', ') : null}
              mono
            />
            <Row label="Source CRS" value={preview.sourceCrs} mono />
          </Section>
        </div>
      );
    case 'pmtiles':
      return (
        <div>
          {hasPreviewableGeometry(preview.bbox, { archiveUrl: preview.archiveUrl }) ? (
            <Section title="2D preview">
              <LayerPreviewMap
                bbox={preview.bbox ?? null}
                center={preview.center ?? null}
                pmtiles={{ archiveUrl: preview.archiveUrl, sourceLayer: preview.selectedSourceLayer }}
              />
              <PreviewMapCaption note="Renders the archive's resolved source-layer over a basemap. If the map is empty here, the data — not the camera — is the problem." />
            </Section>
          ) : null}
          <Section title="PMTiles preview">
            <Row label="Source-layer" value={preview.selectedSourceLayer} mono />
            <Row
              label="Archive"
              value={preview.archiveUrl}
              mono
              onCopy={onCopy}
              copyLabel="PMTiles archive"
              copyValue={preview.archiveUrl}
            />
          </Section>
        </div>
      );
    case 'tileset':
      return (
        <div>
          {preview.tilesetUrl ? (
            <Section title="3D preview">
              <LayerPreview3D tilesetUrl={preview.tilesetUrl} />
              <PreviewMapCaption note="Streams the actual tileset on a turntable. If it stays empty but the main scene shows it, the tile content needs a decoder the preview lacks." />
            </Section>
          ) : null}
          {hasPreviewableGeometry(preview.bbox, null) ? (
            <Section title="2D footprint">
              <LayerPreviewMap bbox={preview.bbox ?? null} center={preview.center ?? null} pmtiles={null} />
              <PreviewMapCaption note="Where the tileset sits on the map." />
            </Section>
          ) : null}
          <Section title="3D Tiles preview">
            <Row
              label="Tileset root"
              value={preview.tilesetUrl}
              mono
              onCopy={onCopy}
              copyLabel="Tileset root"
              copyValue={preview.tilesetUrl}
            />
          </Section>
        </div>
      );
    case 'terrain': {
      const terrainRaster =
        preview.endpoint && preview.endpoint.includes('{z}')
          ? { tiles: withTerrainStyling(preview.endpoint, preview.minmax, preview.colormap) }
          : null;
      return (
        <div>
          {terrainRaster || hasPreviewableGeometry(preview.bbox, null) ? (
            <Section title="2D preview">
              <LayerPreviewMap
                bbox={preview.bbox ?? null}
                center={preview.center ?? null}
                pmtiles={null}
                raster={terrainRaster}
              />
              <PreviewMapCaption
                note={
                  terrainRaster
                    ? 'Live DEM tiles (TiTiler) over a basemap — the actual terrain coverage. If empty, the tile service or the source CRS is the problem.'
                    : 'Terrain renders in the main scene; this shows its coverage footprint.'
                }
              />
            </Section>
          ) : null}
          <Section title="Terrain preview">
            <Row
              label="Endpoint"
              value={preview.endpoint}
              mono
              onCopy={onCopy}
              copyLabel="Terrain endpoint"
              copyValue={preview.endpoint}
            />
            <Row label="No-data" value={preview.nodata} mono />
            <Row label="Colormap" value={preview.colormap} mono />
            <Row
              label="Min / max"
              value={preview.minmax ? `${preview.minmax[0]} … ${preview.minmax[1]}` : null}
              mono
            />
          </Section>
        </div>
      );
    }
    case 'model':
      return (
        <div>
          {preview.modelUrl ? (
            <Section title="3D preview">
              <LayerPreviewModel modelUrl={preview.modelUrl} />
              <PreviewMapCaption note="Loads the GLB/glTF model on a turntable with meshopt + Draco decoders." />
            </Section>
          ) : null}
          {hasPreviewableGeometry(preview.bbox, null) ? (
            <Section title="2D footprint">
              <LayerPreviewMap bbox={preview.bbox ?? null} center={preview.center ?? null} pmtiles={null} />
              <PreviewMapCaption note="Where the model sits on the map." />
            </Section>
          ) : null}
          <Section title="Model preview">
            <Row
              label="Model URL"
              value={preview.modelUrl}
              mono
              onCopy={onCopy}
              copyLabel="Model URL"
              copyValue={preview.modelUrl}
            />
          </Section>
        </div>
      );
    case 'none':
    default:
      return (
        <Section title="Preview">
          <Text variant="body" style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>
            {preview.kind === 'none' ? preview.reason : 'No preview available.'}
          </Text>
        </Section>
      );
  }
}

/** Small muted note under the live preview map. */
function PreviewMapCaption({ note }: { readonly note?: string }) {
  return (
    <Text
      variant="body"
      style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}
    >
      {note ?? 'Live 2D preview · pan & zoom to inspect. Data may differ from the 3D scene.'}
    </Text>
  );
}

/** Tiny normalized rectangle sketch so the operator can sanity-check a bbox. */
function ExtentSketch({ bbox }: { readonly bbox: readonly [number, number, number, number] }) {
  const [minX, minY, maxX, maxY] = bbox;
  const width = maxX - minX;
  const height = maxY - minY;
  const degenerate = !(Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0);
  return (
    <div
      aria-hidden
      style={{
        position: 'relative',
        width: '100%',
        height: 96,
        border: tokens.border.thin,
        borderRadius: tokens.radius.md,
        background: tokens.color.surface[2],
        overflow: 'hidden',
      }}
    >
      {degenerate ? (
        <span
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            color: tokens.color.ink.muted,
            fontSize: tokens.font.size.xs,
          }}
        >
          Degenerate extent
        </span>
      ) : (
        <div
          style={{
            position: 'absolute',
            left: '12%',
            right: '12%',
            top: '14%',
            bottom: '14%',
            border: `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`,
            borderRadius: tokens.radius.sm,
            background: 'transparent',
          }}
        />
      )}
    </div>
  );
}
